import ArrowNav from "@/components/ArrowNav";
import Sitemap from "@/components/Sitemap";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  faBars,
  faCircleUser,
  faCircleInfo,
  faChevronRight,
  faClose,
  faEnvelope,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRegion } from "@/context/RegionContext";

interface AppHeaderProps {
  className?: string;
  textColor?: string;
  borderColor?: string;
  hideMenu?: boolean;
  isSmallLogo?: boolean;
}

const AppHeader: React.FC<AppHeaderProps> = ({
  className,
  textColor = "text-white",
  borderColor = "border-white",
  hideMenu = false,
  isSmallLogo = false,
}) => {
  const navigate = useNavigate();
  const { region } = useRegion();

  const [isOpen, setIsOpen] = useState(false);
  const [expandType, setExpandType] = useState("");

  useEffect(() => {
    if (isOpen) {
      document.body.style.cssText = "overflow: hidden;height: 100vh;";
    } else {
      document.body.style.cssText = "";
    }
  }, [isOpen]);

  return (
    <>
      {/* Hamburger */}
      {isOpen ? (
        <div className="absolute w-full h-screen z-20 flex overflow-hidden">
          <div className="absolute z-10 top-4 md:right-12 flex flex-row justify-evenly md:justify-end w-screen">
            <Button
              className="block md:hidden bg-white border-green-600 border-[1px] text-black px-6 py-8 drop-shadow-none shadow-none shadow-none hover:bg-transparent hover:border-2"
              onClick={() => {
                setIsOpen(false);
                setExpandType("");
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} size="xl" />
            </Button>
            <img src="/logo.svg" className="w-1/3 block md:hidden" />
            <Button
              className="bg-white border-green-600 border-[1px] text-black px-6 py-8 drop-shadow-none shadow-none shadow-none hover:bg-transparent hover:border-2"
              onClick={() => {
                setIsOpen(false);
                setExpandType("");
              }}
            >
              <FontAwesomeIcon icon={faClose} size="xl" />
            </Button>
          </div>
          <div
            className={`bg-black/30 backdrop-blur-sm ${expandType == "" ? "w-0 md:w-2/3 " : "w-0"}`}
            onClick={() => setIsOpen(false)}
          ></div>
          <div
            className={`${expandType == "" ? "w-full md:w-1/3" : "hidden md:block w-1/2"} bg-white font-bold flex flex-col items-end px-12 py-4 space-y-12`}
          >
            <img
              src="/logo.svg"
              className={expandType == "" ? "opacity-0" : "opacity-100"}
            />
            <div className="flex flex-col space-y-8 items-end mt-28 h-[200vh]">
              <ArrowNav
                title="Locations"
                icon={faChevronRight}
                fontColor={
                  expandType == "location" ? "text-green-600" : "text-black"
                }
                fontSize="text-xl"
                hoverBorderColor={
                  expandType == "location"
                    ? "hover:border-green-600"
                    : "hover:border-black"
                }
                borderColor={
                  expandType == "location"
                    ? "border-green-600"
                    : "border-transparent"
                }
                onClick={() => setExpandType("location")}
              />
              <ArrowNav
                title="See all our deals"
                icon={faChevronRight}
                iconColor="text-white"
                fontColor="text-black"
                fontSize="text-xl"
                hoverBorderColor="hover:border-black"
                onClick={() => {
                  window.location.href = `${region.productionUrl}/pages/top-deals`;
                }}
              />
              <ArrowNav
                title="Vehicles"
                icon={faChevronRight}
                fontColor={
                  expandType == "vehicle" ? "text-green-600" : "text-black"
                }
                fontSize="text-xl"
                hoverBorderColor={
                  expandType == "vehicle"
                    ? "hover:border-green-600"
                    : "hover:border-black"
                }
                borderColor={
                  expandType == "vehicle"
                    ? "border-green-600"
                    : "border-transparent"
                }
                onClick={() => setExpandType("vehicle")}
              />
              <ArrowNav
                title="Go to Europcar Business"
                icon={faChevronRight}
                iconColor="text-white"
                fontColor="text-black"
                fontSize="text-xl"
                hoverBorderColor="hover:border-black"
                onClick={() => {
                  window.location.href = `${region.productionUrl}/pages/corporate-car-leasing`;
                }}
              />
              <ArrowNav
                title="Services"
                icon={faChevronRight}
                fontColor={
                  expandType == "service" ? "text-green-600" : "text-black"
                }
                fontSize="text-xl"
                hoverBorderColor={
                  expandType == "service"
                    ? "hover:border-green-600"
                    : "hover:border-black"
                }
                borderColor={
                  expandType == "service"
                    ? "border-green-600"
                    : "border-transparent"
                }
                onClick={() => setExpandType("service")}
              />
            </div>
            <Button className="bg-white border-green-600 border-[1px] text-green-600 font-bold px-5 py-7 drop-shadow-none shadow-none shadow-none hover:bg-transparent hover:border-2">
              <FontAwesomeIcon icon={faEnvelope} className="mr-2" /> Sign Up To
              Newsletter
            </Button>
          </div>
          <div
            className={
              expandType == ""
                ? "w-0"
                : "w-full md:w-1/2 bg-stone-100 border-gray-300 border-[1px] flex flex-col space-y-4 pl-8 pt-32"
            }
          >
            {expandType == "location" ? (
              <div className="overflow-auto">
                <p className="font-bold text-xl">Discover our network</p>
                <Sitemap
                  title="Top cities"
                  contentList={region.topCities.map(city => ({
                    title: city.title,
                    url: `${region.productionUrl}${city.url}`,
                  }))}
                  titleClassName="text-lg"
                  spacing="space-y-4 pt-5"
                  isCollapseNeed={false}
                />
                <a href={`${region.productionUrl}/pages/our-locations`} className="text-sm text-green-600 font-bold cursor-pointer hover:underline">
                  All cities
                </a>
                <Sitemap
                  title="Top countries"
                  contentList={region.topCountries.map(country => ({
                    title: country.title,
                    url: `${region.productionUrl}${country.url}`,
                  }))}
                  titleClassName="text-lg"
                  spacing="space-y-4 pt-5"
                  isCollapseNeed={false}
                />
                <a href="https://www.europcar.com/en/locations" target="_blank" className="text-sm text-green-600 font-bold cursor-pointer hover:underline">
                  All countries
                </a>
              </div>
            ) : expandType == "vehicle" ? (
              <>
                <p className="font-bold text-xl">
                  A fleet that meets your needs
                </p>
                <div className="w-2/3 grid grid-cols-2 gap-4 mt-5">
                  <a href={`${region.productionUrl}/pages/economy-car`}>
                    <div className="hover:bg-white hover:drop-shadow flex flex-col group cursor-pointer">
                      <span className="group-hover:underline text-center text-sm pt-4">
                        Sedans
                      </span>
                      <img src="/home/bezza.png" className="w-24 mx-auto my-8" />
                    </div>
                  </a>
                  <a href={`${region.productionUrl}/pages/premium-car`}>
                    <div className="hover:bg-white hover:drop-shadow flex flex-col group cursor-pointer">
                      <span className="group-hover:underline text-center text-sm pt-4">
                        SUVs
                      </span>
                      <img src="/home/x-trail.png" className="w-24 mx-auto my-8" />
                    </div>
                  </a>
                  <a href={`${region.productionUrl}/pages/family-car`}>
                    <div className="hover:bg-white hover:drop-shadow flex flex-col group cursor-pointer">
                      <span className="group-hover:underline text-center text-sm pt-4">
                        MPVs
                      </span>
                      <img src="/home/innova.png" className="w-24 mx-auto my-8" />
                    </div>
                  </a>
                </div>
              </>
            ) : (
              <div className="w-4/5 grid grid-cols-2 gap-x-4 gap-y-12">
                <Sitemap
                  title="Europcar services"
                  contentList={[
                    {
                      title: "Privilege Loyalty Program",
                      url: `${region.productionUrl}/pages/our-locations`,
                    },
                    {
                      title: "Delivery & Collection",
                      url: `${region.productionUrl}/pages/our-locations`,
                    },
                    {
                      title: "Premium pick up",
                      url: `${region.productionUrl}/pages/our-locations`,
                    },
                    {
                      title: "See all services",
                      url: `${region.productionUrl}/pages/our-locations`,
                    }
                  ]}
                  titleClassName="text-xl"
                />
                <Sitemap
                  title="Our partners"
                  contentList={region.partnerSitemap.map((partner) => ({
                    title: partner.title,
                    url: `${region.productionUrl}${partner.url}`,
                  }))}
                  titleClassName="text-xl"
                />
                {/* Empty placeholder to maintain grid layout */}
                <div></div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <></>
      )}
      {/* Header */}
      <div
        className={cn("flex flex-row justify-between items-center", className)}

      >
        <img
          src={isSmallLogo ? "/small-logo.svg" : "/logo.svg"}
          className={`${isSmallLogo ? 'w-16' : 'w-[104px] sm:w-1/3'} md:w-auto cursor-pointer`}
          alt="Europcar"
          onClick={() => {
            navigate("/")
          }}
        />
        <div className={`flex flex-row items-center gap-1 md:gap-x-3 ${textColor} font-medium`}>
          <div
            className="flex shrink-0 items-center gap-1 rounded-full border border-current/20 p-0.5"
            aria-label="Choose website region"
          >
            {/* Current region */}
            <span
              className="flex h-10 w-10 items-center justify-center gap-1.5 rounded-full bg-current/15 px-2 md:h-8 md:w-auto"
              aria-current="page"
              aria-label={`Current region: ${region.countryName}`}
              title={region.countryName}
            >
              <img
                src={region.flagSvgPath}
                className="h-4 w-6 rounded-[2px] object-cover shadow-sm"
                alt=""
              />
              <span className="hidden text-sm md:inline">{region.code}</span>
            </span>
            {/* Other region */}
            <a
              className="flex h-10 w-10 cursor-pointer items-center justify-center gap-1.5 rounded-full px-2 transition-colors duration-200 hover:bg-current/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current md:h-8 md:w-auto"
              href={region.otherRegion.domain}
              aria-label={`Switch to ${region.otherRegion.label} region`}
              title={`Switch to ${region.otherRegion.label}`}
            >
              <img
                src={region.otherRegion.flagSvgPath}
                className="h-4 w-6 rounded-[2px] object-cover opacity-75 shadow-sm"
                alt=""
              />
              <span className="hidden text-sm md:inline">{region.otherRegion.label}</span>
            </a>
          </div>
          <a
            className={`flex h-11 w-11 cursor-pointer items-center justify-center border-b-2 border-transparent md:h-auto md:w-auto md:justify-start hover:${borderColor}`}
            target="_blank"
            href={`${region.productionUrl}/pages/contact-us`}
            aria-label="Help"
          >
            <FontAwesomeIcon icon={faCircleInfo} className="fa-lg md:mr-2" />
            <span className="sr-only md:not-sr-only">Help</span>
          </a>
          {hideMenu ? (
            <></>
          ) : (
            <button
              type="button"
              className={`flex h-11 w-11 cursor-pointer items-center justify-center border-b-2 border-transparent md:h-auto md:w-auto md:justify-start hover:${borderColor}`}
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Open menu"
              aria-expanded={isOpen}
            >
              <FontAwesomeIcon icon={faBars} className="fa-lg md:mr-2" />
              <span className="sr-only md:not-sr-only">Menu</span>
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default AppHeader;
